import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import EditorialArticle from '../../components/redesign/EditorialArticle.jsx';
import SEO from '../../components/SEO.jsx';

const REPO = 'https://github.com/Aakeeo/llm-wiki-crm';
const KARPATHY = 'https://gist.github.com/karpathy/442a6bf555914893e9891c11519de94f';

export function LLMWikiLeadMagnetA({ onNav }) {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prefer the env-configured lead webhook; fall back to the production
      // n8n endpoint so the form still captures leads if the env var is unset.
      const webhookUrl =
        import.meta.env.VITE_LEAD_WEBHOOK_URL ||
        'https://n8n.aakeeocrm.com/webhook/5fed7f08-2230-4d07-a78b-7db4c26c0246';
      const params = new URLSearchParams({
        ...formData,
        source: 'llm_wiki_lead_magnet',
      });
      await fetch(`${webhookUrl}?${params.toString()}`, {
        method: 'GET',
        mode: 'no-cors',
      });
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'lead_magnet_unlock', {
          event_category: 'lead_capture',
          event_label: 'llm_wiki_crm',
        });
      }
      setSubmitted(true);
    } catch (err) {
      // n8n request may fail silently in local dev — still surface success
      // so the user sees feedback; the webhook side will log misses.
      setSubmitted(true);
    }
  };

  return (
    <EditorialArticle
      navPage="blog"
      onNav={onNav}
      eyebrow="Resources · LLM Wiki CRM"
      title={<>Your CRM is a folder of markdown files. <em>An LLM maintains it for&nbsp;you.</em></>}
      lead="Drop meeting notes in a folder. The LLM extracts contacts, companies, deals and action items — updates every page, maintains cross-references, keeps your pipeline current. Zero lock-in. Your data is just files."
    >
      <SEO
        title="Your CRM is a folder of markdown files | Aakeeo"
        description="An LLM maintains your CRM for you. Drop meeting notes in a folder and let it extract contacts, deals, and tasks. Zero vendor lock-in."
        keywords={['LLM Wiki', 'Markdown CRM', 'Obsidian CRM', 'Andrej Karpathy LLM Wiki']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareSourceCode',
          name: 'LLM-Wiki Open-Source CRM Protocol',
          description:
            'An open-source, markdown-based CRM architecture utilising LLM logic cycles for autonomous pipeline management.',
          codeRepository: REPO,
          programmingLanguage: 'Markdown, Node.js',
          publisher: { '@type': 'Organization', name: 'Aakeeo Technologies' },
        }}
      />

      <section>
        <div className="llmA-grid">
          <div className="llmA-copy articleA-prose" style={{ maxWidth: 'none' }}>
            <p>
              <a href={KARPATHY} target="_blank" rel="noopener noreferrer">
                Built on Andrej Karpathy&apos;s LLM Wiki pattern →
              </a>
            </p>
            <ul>
              <li>No forms. No fields. No data entry.</li>
              <li>Works with Claude, Cursor, or any LLM that reads an instruction file.</li>
              <li>Git-versioned. Your history is auditable by default.</li>
              <li>Self-hosted on your laptop or a private repo.</li>
            </ul>
            <h2>What you get</h2>
            <ul>
              <li>Complete folder scaffold — contacts, companies, deals, interactions, tasks.</li>
              <li>A working <code>CLAUDE.md</code> instruction set that drives ingest, query, update and lint flows.</li>
              <li>Dataview queries for pipeline, activity, stale-deal detection.</li>
              <li>Templater presets for every entity type.</li>
            </ul>
          </div>

          <aside className="llmA-form">
            {!submitted ? (
              <form onSubmit={handleSubmit}>
                <span className="eyebrowA">Get the kit</span>
                <h2>Deploy it to your team.</h2>
                <p>Drop your email and we&apos;ll send the repo link, the deployment notes, and an invite to the monthly &ldquo;owned CRM&rdquo; essay.</p>
                <label className="contactA-field">
                  <span>Name</span>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jane Doe"
                  />
                </label>
                <label className="contactA-field">
                  <span>Work email</span>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jane@company.com"
                  />
                </label>
                <button type="submit" className="btnA-primary">Unlock the kit</button>
                <p className="llmA-note">No drip sequence. One email with the link, plus the monthly essay.</p>
              </form>
            ) : (
              <div className="llmA-success">
                <span className="eyebrowA">CRM kit unlocked</span>
                <h2>Sent. Check your inbox.</h2>
                <p>You&apos;ll find the repo link, deployment notes, and an invite to the monthly essay.</p>
                <div className="llmA-success-cta">
                  <a href={REPO} target="_blank" rel="noopener noreferrer" className="btnA-primary">Open repo</a>
                  <Link to="/contact" className="btnA-ghost">Talk about a team rollout →</Link>
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section>
        <h2 className="serviceA-h2">How it works — the four workflows.</h2>
        <div className="pillA-grid pillA-grid--3">
          <div className="pillA-card">
            <div className="pillA-num">I.</div>
            <h3>Ingest</h3>
            <p>Drop a raw meeting note in <code>_inbox/</code>. The LLM extracts contacts, companies, deals, interactions and tasks — creating and linking files automatically.</p>
          </div>
          <div className="pillA-card">
            <div className="pillA-num">II.</div>
            <h3>Query</h3>
            <p>Ask plain-English questions. &ldquo;What&apos;s our pipeline value?&rdquo; &ldquo;Warmest leads?&rdquo; The LLM synthesises answers with citations across every file.</p>
          </div>
          <div className="pillA-card">
            <div className="pillA-num">III.</div>
            <h3>Update</h3>
            <p>&ldquo;Move the Acme deal to negotiation.&rdquo; &ldquo;Mark Jane inactive.&rdquo; The LLM finds the file, updates YAML frontmatter, logs the change.</p>
          </div>
          <div className="pillA-card">
            <div className="pillA-num">IV.</div>
            <h3>Lint</h3>
            <p>Request a health check — stale deals, missing data, broken links, overdue tasks. Structured report, suggested fixes.</p>
          </div>
          <div className="pillA-card pillA-card--cta">
            <h3>Want team deployment?</h3>
            <p>Multi-user, audit trails and a shared Git remote. We&apos;ll set it up end-to-end.</p>
            <Link to="/contact" className="btnA-primary btnA-primary--sm">Talk to us</Link>
          </div>
        </div>
      </section>

      <section className="ctaA" style={{ padding: 0, marginTop: 48 }}>
        <div className="ctaA-inner">
          <h2>The CRM your team will actually use.</h2>
          <p>Free and open source. Works with any LLM agent. Ships today.</p>
          <div className="ctaA-buttons">
            <a className="btnA-primary" href={REPO} target="_blank" rel="noopener noreferrer">Get it on GitHub</a>
            <Link to="/blog/llm-wiki-crm" className="btnA-ghost">Read the full write-up →</Link>
          </div>
        </div>
      </section>
    </EditorialArticle>
  );
}
